import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPublicProfile, revealContact, getVCardUrl, Profile as ProfileType, ContactInfo } from '../lib/api';
import LanguageSelector from '@/components/ui/LanguageSelector';
import Footer from '../components/ui/Footer';
import VCardInstructions from '../components/profile/VCardInstructions';
import PublicContactPreview from '../components/profile/PublicContactPreview';
import ProfileNotFound from '@/components/profile/ProfileNotFound';

export default function Profile() {
  const { t } = useTranslation();
  // React Router provides publicId directly as a string (not an array like Next.js catch-all)
  const { publicId } = useParams<{ publicId: string }>();

  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadProfile() {
      if (!publicId) {
        setError(t('profile.errors.noId'));
        setLoading(false);
        return;
      }
      try {
        const data = await getPublicProfile(publicId);
        setProfile(data);
      } catch {
        setError(t('profile.errors.notFound'));
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [publicId, t]);

  async function handleRevealContact() {
    if (!publicId) return;
    setRevealing(true);
    try {
      const info = await revealContact(publicId);
      setContactInfo(info);
    } catch {
      setError(t('profile.errors.revealFailed'));
    } finally {
      setRevealing(false);
    }
  }

  async function handleDownloadVCard() {
    if (!contactInfo || !publicId) return;
    setDownloading(true);
    setError(null);

    try {
      const url = getVCardUrl(publicId, contactInfo.vcardToken);
      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired - refresh contact info and retry
          const newInfo = await revealContact(publicId);
          setContactInfo(newInfo);
          const newUrl = getVCardUrl(publicId, newInfo.vcardToken);
          window.location.href = newUrl;
          return;
        }
        throw new Error('Download failed');
      }

      // Success - trigger download via blob
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${profile?.firstName}_${profile?.lastName}.vcf`;
      a.click();
      URL.revokeObjectURL(downloadUrl);
    } catch {
      setError(t('profile.errors.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="bg-gray-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="text-xl font-bold">
                <span className="text-white">MyPink</span>
                <span className="text-mk-pink-400">Contact</span>
              </Link>
              <LanguageSelector variant="light" />
            </div>
          </div>
        </header>
        <div className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mk-pink-600"></div>
        </div>
        <Footer showCta={false} />
      </div>
    );
  }

  if (error || !profile) {
    return <ProfileNotFound />;
  }

  const accentColor = profile.accentColor || '#e91e8c';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-xl font-bold">
              <span className="text-white">MyPink</span>
              <span className="text-mk-pink-400">Contact</span>
            </Link>
            <LanguageSelector variant="light" />
          </div>
        </div>
      </header>

      {/* Main content wrapper */}
      <div className="flex-grow">
        {/* Accent color banner */}
        <div
          className="h-32 w-full"
          style={{ backgroundColor: accentColor }}
        />

        {/* Profile card */}
        <div className="max-w-md mx-auto -mt-16 px-4 pb-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Profile photo */}
          <div className="flex justify-center -mt-0 pt-4">
            <div className="relative">
              {profile.photoUrl ? (
                <img
                  key={profile.photoUrl}
                  src={profile.photoUrl}
                  alt={`${profile.firstName} ${profile.lastName}`}
                  className="w-28 h-28 rounded-full border-4 border-white shadow-lg object-cover"
                />
              ) : (
                <div
                  className="w-28 h-28 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white text-3xl font-bold"
                  style={{ backgroundColor: accentColor }}
                >
                  {profile.firstName[0]}
                  {profile.lastName[0]}
                </div>
              )}
            </div>
          </div>

          {/* Profile info */}
          <div className="text-center px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {profile.firstName} {profile.lastName}
            </h1>
            {profile.tagline && (
              <p className="text-lg text-gray-700 mt-1 italic">
                &quot;{profile.tagline}&quot;
              </p>
            )}
            <p className="text-gray-600 mt-1">{profile.title}</p>
          </div>

          {/* Contact section */}
          <div className="px-6 pb-6">
            {!contactInfo ? (
              <button
                onClick={handleRevealContact}
                disabled={revealing}
                className="w-full py-3 rounded-full text-white font-semibold transition-colors disabled:opacity-50"
                style={{ backgroundColor: accentColor }}
              >
                {revealing ? t('profile.loading') : t('profile.showContact')}
              </button>
            ) : (
              <div className="space-y-3">
                {/* Phone */}
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: accentColor }}
                  >
                    <PhoneIcon />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">{t('profile.phone')}</p>
                    <p className="font-medium text-gray-900">
                      {contactInfo.phone}
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: accentColor }}
                  >
                    <EmailIcon />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">{t('profile.email')}</p>
                    <p className="font-medium text-gray-900">
                      {contactInfo.email}
                    </p>
                  </div>
                </a>

                {/* Website */}
                {profile.website && (
                  <a
                    href={profile.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: accentColor }}
                    >
                      <WebIcon />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">{t('profile.website')}</p>
                      <p className="font-medium text-gray-900">
                        {profile.website.replace(/^https?:\/\//, '')}
                      </p>
                    </div>
                  </a>
                )}

                {/* Address */}
                {contactInfo.address && (contactInfo.address.streetAddress || contactInfo.address.city) && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: accentColor }}
                      >
                        <LocationIcon />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">{t('profile.address')}</p>
                        <p className="font-medium text-gray-900">
                          {contactInfo.address.streetAddress && (
                            <>{contactInfo.address.streetAddress}<br /></>
                          )}
                          {contactInfo.address.postalCode && contactInfo.address.city
                            ? `${contactInfo.address.postalCode} ${contactInfo.address.city}`
                            : contactInfo.address.city}
                          {contactInfo.address.country && (
                            <><br />{contactInfo.address.country}</>
                          )}
                        </p>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            [
                              contactInfo.address.streetAddress,
                              contactInfo.address.postalCode,
                              contactInfo.address.city,
                              contactInfo.address.country
                            ].filter(Boolean).join(', ')
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block mt-2 text-sm font-medium hover:underline"
                          style={{ color: accentColor }}
                        >
                          {t('profile.viewOnMap')}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Download vCard button */}
                <button
                  type="button"
                  onClick={handleDownloadVCard}
                  disabled={downloading}
                  className="w-full mt-4 py-3 rounded-full text-white font-semibold transition-colors disabled:opacity-50"
                  style={{ backgroundColor: accentColor }}
                >
                  {downloading ? t('profile.downloading') : t('profile.downloadVCard')}
                </button>

                {/* vCard import instructions */}
                <VCardInstructions accentColor={accentColor} />

                {/* Contact preview showing how it will look after import */}
                <PublicContactPreview profile={profile} contactInfo={contactInfo} />
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Footer */}
      <Footer showCta={false} />
    </div>
  );
}

function PhoneIcon() {
  return (
    <svg
      className="w-5 h-5 text-white"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg
      className="w-5 h-5 text-white"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function WebIcon() {
  return (
    <svg
      className="w-5 h-5 text-white"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
      />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg
      className="w-5 h-5 text-white"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}
